/**
 * Where a card falls in the run of cards from the same person.
 *
 * A postcard from someone who has written before carries a postmark saying which one it
 * is — "4th" — and the whole of that claim rests on the cards having an order. They do,
 * but not the obvious one. The gallery sorts by received date, and a received date is
 * typed by hand, can be a bare month ("2026-09"), can be blank, and two cards from one
 * person can carry the same one: a pair posted together arrives together. So received
 * date alone leaves ties, and a tie means two cards both calling themselves the 4th.
 *
 * `created_at` breaks them. It is the moment the card was entered, and cards are entered
 * as they arrive, so entry order is arrival order for everything the received date cannot
 * separate. The id cannot do this job — a card added through the form gets a random UUID
 * and an imported draft gets a content hash, and `models.ListDrafts` carries the same
 * warning for the same reason. It is used here only as a last tiebreak, so that a run
 * whose dates are identical to the second still comes out in one stable order rather than
 * renumbering itself between renders.
 *
 * The comparison is deliberately the exact reverse of the server's
 * `ORDER BY received_date DESC, created_at DESC`, so a card's number agrees with where it
 * sits in the gallery rather than being a second opinion about the same collection.
 *
 * Which means the gallery's own two date sorts have to break their ties on `created_at`
 * as well, and they do — see the comparator in Home.tsx. A sort that ties on received date
 * alone falls back to whatever order the rows arrived in, and a stable sort then hands back
 * a same-day pair newest-entered first even while the page is ascending, which shows the
 * 4th above the 3rd. The two places are a pair: changing the fields here without changing
 * them there puts the numbers back out of step with the page.
 */

/** The fields a card needs to take its place in a run. */
type RunnableCard = {
  id: string;
  senderUser: string;
  receivedDate: string;
  createdAt: string;
};

export type SenderRun = {
  /** How many cards that sender has sent, this one included. Always 2 or more. */
  total: number;
  /** This card's place in the run, oldest first: 1 is the first that arrived. */
  nth: number;
};

/**
 * The one spelling of a sender that everything counting senders must agree on.
 *
 * Trimmed as well as lowercased: a name typed with a trailing space is the same person,
 * and counting them as two would show a second sender in the statistics while leaving
 * both of their cards unstamped — the failure would be two numbers quietly disagreeing,
 * with nothing on screen to say which was wrong.
 */
export function senderKey(senderUser: string) {
  return senderUser.trim().toLowerCase();
}

/**
 * The sender filter after a tap on a sender's name: that sender, or nobody when the name
 * tapped is the one already picked (#74). Compared through senderKey, the same as the
 * filter itself, so a second card from "marta " undoes a filter set from "Marta".
 */
export function toggledSender(current: string, tapped: string) {
  return current && senderKey(current) === senderKey(tapped) ? "" : tapped;
}

/** "1st", "2nd", "3rd", "4th" — and "11th" through "13th", which break the pattern. */
export function ordinal(n: number) {
  const teens = n % 100;
  if (teens >= 11 && teens <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

/** Oldest first, and the exact reverse of how the server hands the gallery back. */
function byArrival(a: RunnableCard, b: RunnableCard) {
  return a.receivedDate.localeCompare(b.receivedDate)
    || a.createdAt.localeCompare(b.createdAt)
    || a.id.localeCompare(b.id);
}

/**
 * Every card that belongs to a run, keyed by card id.
 *
 * A sender with one card is absent rather than present with `{total: 1, nth: 1}`: there is
 * no postmark for a first card from a new person, and a caller that can write
 * `runs.get(card.id)` cannot forget to check.
 */
export function senderRuns(postcards: readonly RunnableCard[]): Map<string, SenderRun> {
  const bySender = new Map<string, RunnableCard[]>();
  for (const card of postcards) {
    const key = senderKey(card.senderUser);
    // A card with no sender name at all belongs to no run. Grouping them would make one
    // run out of unrelated people and stamp every card in it.
    if (!key) continue;
    const run = bySender.get(key);
    if (run) run.push(card);
    else bySender.set(key, [card]);
  }

  const runs = new Map<string, SenderRun>();
  for (const cards of bySender.values()) {
    if (cards.length < 2) continue;
    // Sorting the group, never the argument: the gallery holds the array this was called
    // with and has its own order to keep.
    cards.sort(byArrival);
    cards.forEach((card, index) => runs.set(card.id, { total: cards.length, nth: index + 1 }));
  }
  return runs;
}
